import { useCallback, useEffect, useRef, useState } from 'react';
import API_CONFIG from '../lib/config';
import { GiftItem } from '../types';

const PAGE_SIZE = 12;

interface Filters {
  type?: 'product' | 'subscription';
  category?: string;
  q?: string;
}

/**
 * Cursor-paginated product list. Changing filters starts a fresh list; loadMore() appends the next page.
 * Stale responses (from earlier filters) are ignored.
 */
export function useProductCatalog({ type = 'product', category, q }: Filters) {
  const [items, setItems] = useState<GiftItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const generation = useRef(0);
  const inFlight = useRef(false);

  const fetchPage = useCallback(
    async (fromCursor: string | null, gen: number) => {
      inFlight.current = true;
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ limit: String(PAGE_SIZE), type });
        if (category && category !== 'all') params.set('category', category);
        if (q && q.trim()) params.set('q', q.trim());
        if (fromCursor) params.set('cursor', fromCursor);
        const response = await fetch(`${API_CONFIG.BASE_URL}/items?${params.toString()}`);
        const json = await response.json().catch(() => null);
        if (!response.ok || !json?.success) throw new Error(json?.message || 'We couldn’t load products right now.');
        if (gen !== generation.current) return;
        const page: GiftItem[] = (json.data || []).map((item: GiftItem) => ({ ...item, id: item._id }));
        setItems((prev) => (fromCursor ? [...prev, ...page.filter((p) => !prev.some((x) => x._id === p._id))] : page));
        setCursor(json.nextCursor || null);
        setDone(!json.nextCursor);
      } catch (e) {
        if (gen === generation.current) setError((e as Error).message);
      } finally {
        if (gen === generation.current) {
          setLoading(false);
          inFlight.current = false;
        }
      }
    },
    [type, category, q]
  );

  useEffect(() => {
    generation.current += 1;
    setItems([]);
    setCursor(null);
    setDone(false);
    void fetchPage(null, generation.current);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (inFlight.current || done || !cursor) return;
    void fetchPage(cursor, generation.current);
  }, [cursor, done, fetchPage]);

  const retry = useCallback(() => void fetchPage(cursor, generation.current), [cursor, fetchPage]);

  return { items, loading, error, hasMore: !done, loadMore, retry };
}

export async function fetchCategories(type: 'product' | 'subscription' = 'product'): Promise<Array<{ category: string; count: number }>> {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/items/categories?type=${type}`);
    const json = await response.json();
    return json?.success ? json.data : [];
  } catch {
    return [];
  }
}

/** BOQBOX service + payment fees for an order subtotal (computed by the server). */
export async function fetchFees(amount: number): Promise<{ subtotal: number; fees: number; total: number } | null> {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/fees?amount=${encodeURIComponent(String(Math.max(0, amount)))}`);
    const json = await response.json();
    return json?.success ? json.data : null;
  } catch {
    return null;
  }
}
